import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TooltipModule } from 'primeng/tooltip';
import { SideMenuBarComponent } from './components/side-menu-bar/side-menu-bar.component';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { TopHeaderComponent } from './components/top-header/top-header.component';
import { NavigationItem } from './interfaces/navigationList.interface';
import { PageContainerConfigurations } from './interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from './services/page-container.service';
import { MenuTool } from './interfaces/MenuTool.interface';
import { tap } from 'rxjs/operators';
import { ViewDetectionService } from './services/view-detection.service';
import { SideMenuDrawerComponent } from './components/side-menu-drawer/side-menu-drawer.component';
import { MobileRightMenuComponent } from './components/mobile-right-menu/mobile-right-menu.component';
import { FiltersRegion, FiltersLocation } from './interfaces/region.interface';
import { MobileHeaderFiltersComponent } from './components/mobile-header-filters/mobile-header-filters.component';
import { BCTopHeaderFiltersComponent } from './components/bc-top-header-filters/bc-top-header-filters.component';
import { Observable } from 'rxjs';
import { AboutModalComponent } from '../modals/about-modal/about-modal.component';
import { ProfileModalService } from '../../../shared/services/profileModal.service';
import { ProfileModalComponent } from '../modals/profile-modal/profile-modal.component';

@Component({
  selector: 'st-pages-container',
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TopHeaderComponent,
    SideMenuComponent,
    SideMenuBarComponent,
    SideMenuDrawerComponent,
    MobileRightMenuComponent,
    MobileHeaderFiltersComponent,
    BCTopHeaderFiltersComponent,
    AboutModalComponent,
    ProfileModalComponent,
  ],
  templateUrl: './pages-container.component.html',
  styleUrls: ['./pages-container.component.scss'],
})
export class PagesContainerComponent {
  @Input({ required: true }) navigationList: NavigationItem[] = [];
  tools = input.required<MenuTool[]>();
  configurations = input.required<PageContainerConfigurations>();
  regions = input.required<FiltersRegion[]>();
  locations = input.required<FiltersLocation[]>();
  locationsFilters = output<FiltersLocation[]>();
  public isAboutModalVisible = signal<boolean>(false);
  public isProfileModalVisible = signal<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);

  isFiltersMenuOpened = signal(false);

  selectedRegions: FiltersRegion[] = [];
  selectedLocations: FiltersLocation[] = [];

  gridView$ = this.viewDetection.gridView.pipe(
    tap(view => {
      if (view === 'large' || view === 'xLarge') {
        this.pageContainerService.expandMenuDrawer$.next(false);
        this.pageContainerService.expandFilterDrawer$.next(false);
      }
    }),
  );

  menuExpanded$: Observable<any> = this.pageContainerService.menuExpanded;
  expandMenuDrawer$ = this.pageContainerService.expandMenuDrawer$;
  expandFilterDrawer$ = this.pageContainerService.expandFilterDrawer$.pipe(
    tap(x => {
      if (x) this.pageContainerService.expandMenuDrawer$.next(!x);

      if (x !== this.isFiltersMenuOpened()) {
        this.isFiltersMenuOpened.set(x);
      }
    }),
  );

  constructor(
    readonly pageContainerService: PageContainerService,
    readonly viewDetection: ViewDetectionService,
    readonly cd: ChangeDetectorRef,
    readonly profileModalService: ProfileModalService,
  ) {}

  ngOnInit(): void {
    this.profileModalService.modalOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.openAboutModal();
      });

    this.profileModalService.profileModalOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.openProfileModal();
      });
  }

  ngAfterViewInit() {
    this.checkSetFiltersDefault();
  }

  filter(event: FiltersLocation[]) {
    this.checkSetFiltersDefault();
    this.locationsFilters.emit(this.selectedLocations);
  }

  expandFilters() {
    this.isFiltersMenuOpened.update(x => !x);
    this.pageContainerService.expandFilterDrawer$.next(
      this.isFiltersMenuOpened(),
    );
  }

  checkSetFiltersDefault() {
    if (!this.configurations().hasHeaderFilters) {
      return; // Exit early if no filters are needed
    }

    // Set default region if none is selected
    if (this.selectedRegions.length === 0 && this.regions().length > 0) {
      this.selectedRegions = [this.regions()[0]];
    }

    // Set default location if none is selected and a region is available
    if (
      this.selectedLocations.length === 0 &&
      this.selectedRegions.length > 0
    ) {
      const regionId = this.selectedRegions[0]?.id;
      const defaultLocation = this.locations().find(
        loc => loc.regionId === regionId,
      );

      if (defaultLocation) {
        this.selectedLocations = [defaultLocation];
      } else {
        throw new Error(
          `Cannot find a location for region with ID: ${regionId}`,
        );
      }
    }

    this.cd.detectChanges();
  }

  /**
   *  Method about modal
   */
  public openAboutModal(): void {
    this.isAboutModalVisible.set(true);
  }

  public closeAboutModal(): void {
    this.isAboutModalVisible.set(false);
  }

  /**
   *  Profile about modal
   */
  public openProfileModal(): void {
    this.isProfileModalVisible.set(true);
  }

  public closeProfileModal(): void {
    this.isProfileModalVisible.set(false);
  }
}
